import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  FollowArtistsFragmentDoc,
  MyPreSaleEventConnectionFragmentDoc,
  MyProfileQuery,
  MyProfileQueryVariables,
  OrganizationArtistRankingsConnectionFragmentDoc,
  RankingStatus,
  UserConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims, user: currentUser } = useCurrentUser()

  const { data, loading, fetchMore, networkStatus } = useQuery<
    MyProfileQuery,
    MyProfileQueryVariables
  >(QUERY, {
    variables: {
      artistId: config.artistId,
      fetchMyPreSaleEvents: false,
      isStandalone: config.applicationType === 'standalone',
    },
    notifyOnNetworkStatusChange: true,
    async onCompleted({ myPreSaleEvents, artist }) {
      if (artist) {
        currentArtistVar(artist)
      }
      // fetch more item if data is empty and have next page
      if (
        !myPreSaleEvents?.edges?.length &&
        myPreSaleEvents?.pageInfo.hasNextPage
      ) {
        await fetchMore({
          variables: {
            artistId: config.artistId,
            fetchMyPreSaleEvents: true,
            pageInput: { cursor: myPreSaleEvents.pageInfo.endCursor },
            isStandalone: config.applicationType === 'standalone',
          },
        })
      }
    },
  })
  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      fetchingMyPreSaleEvents:
        networkStatus === NetworkStatus.setVariables ||
        networkStatus === NetworkStatus.fetchMore,
      currentUser: currentUser ?? null,
      user: data?.user ?? null,
      isCustom: !!(
        data?.user?.customProfile?.avatarItem ||
        data?.user?.customProfile?.wallpaperItem
      ),
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      artistRankings:
        data?.organizationArtistRankings?.edges.filter(
          ({ node }) => node.status === RankingStatus.Active
        ) ?? null,
      events: data?.myPreSaleEvents ?? null,
    },
    actions: {},
  }
})

const QUERY = gql`
  query MyProfile(
    $artistId: ID
    $pageInput: PageInput
    $fetchMyPreSaleEvents: Boolean!
    $isStandalone: Boolean!
  ) {
    user @skip(if: $fetchMyPreSaleEvents) {
      ...UserConnection
      organizationFollowees {
        ...FollowArtists
      }
    }
    artist(id: $artistId) @include(if: $isStandalone) {
      ...ArtistBottomTab
      currentLive {
        id
      }
    }
    prepaidPointBalance @skip(if: $fetchMyPreSaleEvents) {
      prepaidPointId
      balance
    }
    organizationArtistRankings @skip(if: $fetchMyPreSaleEvents) {
      ...OrganizationArtistRankingsConnection
    }
    myPreSaleEvents(filter: all, pageInput: $pageInput) {
      ...MyPreSaleEventConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${UserConnectionFragmentDoc}
  ${FollowArtistsFragmentDoc}
  ${OrganizationArtistRankingsConnectionFragmentDoc}
  ${MyPreSaleEventConnectionFragmentDoc}
`
